/**
 * Inventory Batch Types
 * Type definitions for inventory batch management
 *
 * @author SAUBHAGYA Development Team
 * @since US-BATCH-001
 */

// ============================================
// ENUMS
// ============================================

/**
 * Product Type Enum for Biogas Products
 */
export enum ProductType {
  COMPRESSED = 'COMPRESSED',
  LIQUIFIED = 'LIQUIFIED',
  BIO_CNG = 'BIO_CNG'
}

/**
 * Quantity Unit Enum for Inventory Measurement
 */
export enum QuantityUnit {
  KG = 'KG',
  LITERS = 'LITERS',
  CUBIC_METERS = 'CUBIC_METERS',
  TONS = 'TONS'
}

/**
 * Quality Grade Enum for Biogas Products
 */
export enum QualityGrade {
  A = 'A',
  B = 'B',
  C = 'C'
}

/**
 * Inventory Batch Status Enum
 */
export enum InventoryBatchStatus {
  AVAILABLE = 'AVAILABLE',
  ALLOCATED = 'ALLOCATED',
  SOLD = 'SOLD',
  EXPIRED = 'EXPIRED',
  QUARANTINED = 'QUARANTINED'
}

// ============================================
// REQUEST/RESPONSE INTERFACES
// ============================================

/**
 * Request DTO for creating a new inventory batch
 */
export interface CreateBatchRequest {
  productType: ProductType;
  productionDate: string; // ISO 8601 format: "yyyy-MM-dd"
  expiryDate: string; // ISO 8601 format: "yyyy-MM-dd"
  initialQuantity: number;
  quantityUnit: QuantityUnit;
  qualityGrade: QualityGrade;
  purityPercentage: number;
  pressure?: number;
  storageLocation: string;
  pesoCompliant?: boolean;
}

/**
 * Request DTO for updating batch quality grade
 * @since US-BATCH-003
 */
export interface UpdateQualityRequest {
  qualityGrade: QualityGrade;
  purityPercentage: number;
  testDate: string; // ISO 8601 format: "yyyy-MM-dd"
  remarks: string;
}

/**
 * PESO Certificate information
 */
export interface PESOCertificate {
  number: string;
  inspector: string;
  issueDate: string; // ISO 8601 format
  expiryDate: string; // ISO 8601 format
  fileUrl?: string;
}

/**
 * Batch allocation to orders
 */
export interface BatchAllocation {
  orderId: string;
  orderNumber: string;
  customerName: string;
  quantity: number;
  orderDate: string; // ISO 8601 format
  orderStatus: string;
}

/**
 * Audit log entry for batch operations
 */
export interface BatchAuditLogEntry {
  id: string;
  timestamp: string; // ISO 8601 format
  action: string;
  performedBy: string;
  details: string;
  status: 'success' | 'warning' | 'error' | 'info';
}

/**
 * Response DTO for inventory batch operations
 */
export interface BatchResponse {
  id: number;
  batchNumber: string;
  productType: ProductType;
  productionDate: string; // ISO 8601 format: "yyyy-MM-dd"
  expiryDate: string; // ISO 8601 format: "yyyy-MM-dd"
  initialQuantity: number;
  currentQuantity: number;
  availableQuantity: number;
  allocatedQuantity: number;
  quantityUnit: QuantityUnit;
  qualityGrade: QualityGrade;
  purityPercentage: number;
  pressure?: number;
  storageLocation: string;
  batchStatus: InventoryBatchStatus;
  pesoCompliant: boolean;
  pesoCertificate?: PESOCertificate;
  allocations?: BatchAllocation[];
  createdBy: string;
  createdAt: string; // ISO 8601 format with time
  updatedBy?: string;
  updatedAt?: string; // ISO 8601 format with time
  externalId?: string;
  clusterId?: string;
  productionBatchId?: string;

  // Calculated fields (from backend)
  daysUntilExpiry?: number;
  utilizationPercentage?: number;
}

/**
 * Generic API Response wrapper
 */
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  errors?: string[];
}

// ============================================
// DISPLAY HELPERS
// ============================================

/**
 * Product Type display configurations
 */
export const ProductTypeConfig = {
  [ProductType.COMPRESSED]: {
    label: 'Compressed Biogas',
    abbreviation: 'CBG',
    description: 'Stored at high pressure (200-250 bar)',
    icon: '🔵'
  },
  [ProductType.LIQUIFIED]: {
    label: 'Liquified Biogas',
    abbreviation: 'LBG',
    description: 'Stored at very low temperature (-162°C)',
    icon: '❄️'
  },
  [ProductType.BIO_CNG]: {
    label: 'Bio-Compressed Natural Gas',
    abbreviation: 'Bio-CNG',
    description: 'Purified biogas meeting CNG standards',
    icon: '🟢'
  }
};

/**
 * Quantity Unit display configurations
 */
export const QuantityUnitConfig = {
  [QuantityUnit.KG]: {
    label: 'Kilograms',
    symbol: 'kg',
    description: 'Weight-based measurement'
  },
  [QuantityUnit.LITERS]: {
    label: 'Liters',
    symbol: 'L',
    description: 'Volume-based measurement (liquid)'
  },
  [QuantityUnit.CUBIC_METERS]: {
    label: 'Cubic Meters',
    symbol: 'm³',
    description: 'Volume-based measurement (gas)'
  },
  [QuantityUnit.TONS]: {
    label: 'Tons',
    symbol: 't',
    description: 'Bulk weight measurement'
  }
};

/**
 * Quality Grade display configurations
 */
export const QualityGradeConfig = {
  [QualityGrade.A]: {
    label: 'Grade A - Premium',
    description: 'Purity: 95.0% - 100.0%',
    minPurity: 95.0,
    maxPurity: 100.0,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-300'
  },
  [QualityGrade.B]: {
    label: 'Grade B - Standard',
    description: 'Purity: 85.0% - 94.9%',
    minPurity: 85.0,
    maxPurity: 94.9,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-300'
  },
  [QualityGrade.C]: {
    label: 'Grade C - Basic',
    description: 'Purity: 70.0% - 84.9%',
    minPurity: 70.0,
    maxPurity: 84.9,
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-300'
  }
};

/**
 * Batch Status display configurations
 */
export const BatchStatusConfig = {
  [InventoryBatchStatus.AVAILABLE]: {
    label: 'Available',
    color: 'text-green-700',
    bgColor: 'bg-green-100',
    icon: '✓'
  },
  [InventoryBatchStatus.ALLOCATED]: {
    label: 'Allocated',
    color: 'text-blue-700',
    bgColor: 'bg-blue-100',
    icon: '📦'
  },
  [InventoryBatchStatus.SOLD]: {
    label: 'Sold',
    color: 'text-gray-700',
    bgColor: 'bg-gray-100',
    icon: '💰'
  },
  [InventoryBatchStatus.EXPIRED]: {
    label: 'Expired',
    color: 'text-red-700',
    bgColor: 'bg-red-100',
    icon: '⚠️'
  },
  [InventoryBatchStatus.QUARANTINED]: {
    label: 'Quarantined',
    color: 'text-orange-700',
    bgColor: 'bg-orange-100',
    icon: '🚫'
  }
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Determine quality grade from purity percentage
 */
export function getGradeFromPurity(purity: number): QualityGrade | null {
  if (purity < 0 || purity > 100) return null;
  if (purity >= 95.0) return QualityGrade.A;
  if (purity >= 85.0) return QualityGrade.B;
  if (purity >= 70.0) return QualityGrade.C;
  return null;
}

/**
 * Validate if purity matches the selected grade
 */
export function isPurityValidForGrade(purity: number, grade: QualityGrade): boolean {
  const config = QualityGradeConfig[grade];
  return purity >= config.minPurity && purity <= config.maxPurity;
}

/**
 * Calculate days until expiry
 */
export function calculateDaysUntilExpiry(expiryDate: string): number {
  const expiry = new Date(expiryDate);
  const today = new Date();
  const diffTime = expiry.getTime() - today.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Check if batch is expiring soon (within 30 days)
 */
export function isExpiringSoon(expiryDate: string): boolean {
  const days = calculateDaysUntilExpiry(expiryDate);
  return days <= 30 && days > 0;
}

/**
 * Check if batch is expired
 */
export function isExpired(expiryDate: string): boolean {
  return calculateDaysUntilExpiry(expiryDate) < 0;
}

/**
 * Format quantity with unit
 */
export function formatQuantity(quantity: number, unit: QuantityUnit): string {
  const config = QuantityUnitConfig[unit];
  return `${quantity.toLocaleString()} ${config.symbol}`;
}

// ============================================
// PESO COMPLIANCE (US-BATCH-004)
// ============================================

/**
 * Request DTO for marking batch as PESO compliant
 * @since US-BATCH-004
 */
export interface PesoComplianceRequest {
  certificateNumber: string; // Format: PESO-YYYY-XXXXXX
  issueDate: string; // ISO 8601 format: "yyyy-MM-dd"
  expiryDate: string; // ISO 8601 format: "yyyy-MM-dd"
  inspectorName: string;
  remarks?: string;
}

/**
 * Response DTO for PESO compliance marking
 * @since US-BATCH-004
 */
export interface PesoComplianceResponse {
  batchId: number;
  batchNumber: string;
  certificateNumber: string;
  certificateUrl: string;
  daysUntilExpiry: number;
  markedBy: string;
  markedAt: string; // ISO 8601 format with time
}

/**
 * Validate PESO certificate number format
 * Format: PESO-YYYY-XXXXXX (e.g., PESO-2024-001234)
 */
export function isValidPesoCertificateNumber(certNumber: string): boolean {
  const regex = /^PESO-\d{4}-\d{6}$/;
  return regex.test(certNumber);
}

/**
 * Check if PESO certificate is expiring soon (within specified days)
 */
export function isPesoCertificateExpiringSoon(expiryDate: string, days: number = 30): boolean {
  const daysUntilExpiry = calculateDaysUntilExpiry(expiryDate);
  return daysUntilExpiry <= days && daysUntilExpiry > 0;
}

/**
 * Check if PESO certificate is expired
 */
export function isPesoCertificateExpired(expiryDate: string): boolean {
  return calculateDaysUntilExpiry(expiryDate) < 0;
}
